/* eslint-disable no-console */
import { fromJS } from 'immutable';
import { createSelector } from 'reselect';
import { PREPAID } from '../../constants/agreementTypes';
import { OLD_GRAPH_VIEW } from '../../constants/graphViews';
import { FAILED_DEACTIVATION, DEACTIVATED } from '../../constants/ServiceContractStatuses';
import { GetUser, UpdatePrimaryPhone, UpdateBillingPreference, GetProspect, GetSalesRep } from '../../api/Profile';
import { GetCapDocs } from '../../api/CAPDocs';
import { GetCapEsignUrl } from '../../api/CAPEsignUrl';

// Helpers
const getCurrentOpptyFromUser = user => {
  const opptyId = user?.get('opptyId');
  return user?.get('opportunities').find(oppty => oppty.opptyId === opptyId);
};

// ACTIONS TYPES
export const FETCH_USER_SUCCESS = 'customer-portal/user/FETCH_USER_SUCCESS';
export const FETCH_USER_FAILURE = 'customer-portal/user/FETCH_USER_FAILURE';
export const FETCHING_USER = 'customer-portal/user/FETCHING_USER';
export const SET_SHOULD_FETCH_USER = 'customer-portal/user/SET_SHOULD_FETCH_USER';
export const FETCHING_PROSPECT = 'customer-portal/user/FETCHING_PROSPECT';
export const FETCH_PROSPECT_SUCCESS = 'customer-portal/user/FETCH_PROSPECT_SUCCESS';
export const FETCH_PROSPECT_FAILURE = 'customer-portal/user/FETCH_PROSPECT_FAILURE';
export const UPDATE_PROSPECT_ACTIVE_CHART = 'customer-portal/user/UPDATE_PROSPECT_ACTIVE_CHART';
export const UPDATING_USER = 'customer-portal/user/UPDATING_USER';
export const SET_USER_HAS_AUTOPAY_TRUE = 'customer-portal/user/SET_USER_HAS_AUTOPAY_TRUE';
export const UPDATE_PHONE_SUCCESS = 'customer-portal/user/UPDATE_PHONE_SUCCESS';
export const UPDATE_PHONE_FAILURE = 'customer-portal/user/UPDATE_PHONE_FAILURE';
export const UPDATE_BILLING_PREFERENCE_SUCCESS = 'customer-portal/user/UPDATE_BILLING_PREFERENCE_SUCCESS';
export const UPDATE_BILLING_PREFERENCE_FAILURE = 'customer-portal/user/UPDATE_BILLING_PREFERENCE_FAILURE';
export const UPDATE_USER_ACCOUNT = 'customer-portal/user/UPDATE_USER_ACCOUNT';
export const UPDATE_USER_ERROR_CLEAR = 'customer-portal/user/UPDATE_USER_ERROR_CLEAR';
export const UPDATE_MOBILE_PHONE = 'customer-portal/user/UPDATE_MOBILE_PHONE';
export const FETCH_SALESREP_SUCCESS = 'customer-portal/system/FETCH_SALESREP_SUCCESS';
export const FETCH_SALESREP_FAILURE = 'customer-portal/system/FETCH_SALESREP_FAILURE';
export const FETCHING_SALESREP = 'customer-portal/system/FETCHING_SALESREP';

export const FETCHING_CAP_DOCS = 'customer-portal/user/FETCHING_CAP_DOCS';
export const FETCH_CAP_DOCS_SUCCESS = 'customer-portal/user/FETCH_CAP_DOCS_SUCCESS';
export const FETCH_CAP_DOCS_FAILURE = 'customer-portal/user/FETCH_CAP_DOCS_FAILURE';
export const RESET_CAP_DOCS = 'customer-portal/user/RESET_CAP_DOCS';

export const FETCHING_CAP_ESIGN_URL = 'customer-portal/user/FETCHING_CAP_ESIGN_URL';
export const FETCH_CAP_ESIGN_URL_SUCCESS = 'customer-portal/user/FETCH_CAP_ESIGN_URL_SUCCESS';
export const FETCH_CAP_ESIGN_URL_FAILURE = 'customer-portal/user/FETCH_CAP_ESIGN_URL_FAILURE';
export const RESET_CAP_ESIGN_URL = 'customer-portal/user/RESET_CAP_ESIGN_URL';
export const SET_PROJECT_ID = 'customer-portal/user/SET_PROJECT_ID';

// ACTION CREATORS
export const fetchUserSuccess = user => ({
  type: FETCH_USER_SUCCESS,
  payload: user,
});

export const fetchUserFailure = err => ({
  type: FETCH_USER_FAILURE,
  payload: err,
});

export const fetchingUser = () => ({
  type: FETCHING_USER,
});

export const setShouldFetchUser = () => ({
  type: SET_SHOULD_FETCH_USER,
});

export const fetchingProspect = () => ({
  type: FETCHING_PROSPECT,
});

export const fetchProspectSuccess = prospect => ({
  type: FETCH_PROSPECT_SUCCESS,
  payload: prospect,
});

export const fetchProspectFailure = err => ({
  type: FETCH_PROSPECT_FAILURE,
  payload: err,
});

export const updateProspectActiveChart = activeChart => ({
  type: UPDATE_PROSPECT_ACTIVE_CHART,
  activeChart,
});

export const updatingUser = () => ({
  type: UPDATING_USER,
});

export const setUserHasAutopayToTrue = currentOpptyIndex => ({
  type: SET_USER_HAS_AUTOPAY_TRUE,
  payload: {
    currentOpptyIndex,
  },
});

export const updatePrimaryPhoneSuccess = payload => ({
  type: UPDATE_PHONE_SUCCESS,
  payload,
});

export const updatePrimaryPhoneFailure = e => ({
  type: UPDATE_PHONE_FAILURE,
  payload: e,
});

export const updateBillingPreferenceSuccess = payload => ({
  type: UPDATE_BILLING_PREFERENCE_SUCCESS,
  payload,
});

export const updateBillingPreferenceFailure = e => ({
  type: UPDATE_BILLING_PREFERENCE_FAILURE,
  payload: e,
});

export const updateUserAccountSuccess = payload => ({
  type: UPDATE_USER_ACCOUNT,
  payload,
});

export const updateMobilePhoneNumberSuccess = payload => ({
  type: UPDATE_MOBILE_PHONE,
  payload,
});
export const updateUserErrorClear = () => ({
  type: UPDATE_USER_ERROR_CLEAR,
});

export const fetchSalesRepSuccess = salesRep => ({
  type: FETCH_SALESREP_SUCCESS,
  payload: salesRep,
});

export const fetchSalesRepFailure = err => ({
  type: FETCH_SALESREP_FAILURE,
  payload: err,
});

export const fetchingCapDocs = () => ({
  type: FETCHING_CAP_DOCS,
});

export const fetchCapDocsSuccess = payload => ({
  type: FETCH_CAP_DOCS_SUCCESS,
  payload,
});

export const fetchCapDocsFailure = err => ({
  type: FETCH_CAP_DOCS_FAILURE,
  payload: err,
});

export const resetCapDocs = () => ({
  type: RESET_CAP_DOCS,
});

export const fetchingCapEsignUrl = () => ({
  type: FETCHING_CAP_ESIGN_URL,
});

export const fetchCapEsignUrlSuccess = payload => ({
  type: FETCH_CAP_ESIGN_URL_SUCCESS,
  payload,
});

export const fetchCapEsignUrlFailure = err => ({
  type: FETCH_CAP_ESIGN_URL_FAILURE,
  payload: err,
});

export const resetCapEsignUrl = () => ({
  type: RESET_CAP_ESIGN_URL,
});

export const setUserProjectId = projectId => ({
  type: SET_PROJECT_ID,
  payload: projectId,
});

// THUNKS
export const fetchUser = () => async dispatch => {
  dispatch(fetchingUser());
  try {
    const { data } = await GetUser();
    dispatch(fetchUserSuccess(data));
  } catch (error) {
    dispatch(fetchUserFailure(error));
  }
};

export const fetchProspect = prospectId => async dispatch => {
  dispatch(fetchingProspect());
  try {
    const { data } = await GetProspect(prospectId);
    dispatch(fetchProspectSuccess(data));
  } catch (e) {
    dispatch(fetchProspectFailure(e));
  }
};

export const shouldFetchUser = (userState, authenticationState) => {
  //eslint-disable-line
  // We should only try to fetch the user if they are logged in and haven't been loaded yet
  return authenticationState?.get('isLoggedIn') && !userState?.get('email') && !userState?.get('loading') && !userState?.get('fetchUserFailed');
};

export const fetchUserIfNeeded = () => (dispatch, getState) => {
  if (!shouldFetchUser(getState()?.user, getState()?.authentication)) {
    return null;
  }

  return dispatch(fetchUser());
};

export const shouldFetchProspect = userState => !userState.get('prospect') && !userState.get('loadingProspect');

export const fetchProspectIfNeeded = prospectId => (dispatch, getState) => {
  if (!shouldFetchProspect(getState().user)) {
    return null;
  }

  return dispatch(fetchProspect(prospectId));
};

export const updatePrimaryPhoneNumber = phoneNumber => async dispatch => {
  dispatch(updatingUser());

  try {
    await UpdatePrimaryPhone(phoneNumber);
    dispatch(updatePrimaryPhoneSuccess({ phoneNumber }));
    dispatch(fetchUser());
  } catch (e) {
    dispatch(updatePrimaryPhoneFailure(e));
  }
};

export const updateMobilePhoneNumber = mobileNumber => async dispatch => {
  dispatch(updatingUser());
  await dispatch(updateMobilePhoneNumberSuccess({ mobilePhoneNumber: mobileNumber.replace('+1', '') }));
  dispatch(fetchUser());
};

export const updateCurrentContractToHaveAutopaySetTrue = () => async (dispatch, getState) => {
  const { user } = getState();
  const currentOpptyIndex = user?.get('opportunities').findIndex(oppty => oppty.opptyId === user.get('opptyId'));

  if (currentOpptyIndex >= 0) {
    dispatch(setUserHasAutopayToTrue(currentOpptyIndex));
  }
};

export const updateBillingNotificationPreference = (billingPreference, contractId) => async (dispatch, getState) => {
  dispatch(updatingUser());
  const { user } = getState();
  const currentOpptyIndex = user.get('opportunities').findIndex(oppty => oppty.opptyId === user.get('opptyId'));
  const uppercaseBillingPreference = billingPreference.charAt(0).toUpperCase() + billingPreference.slice(1);

  try {
    await UpdateBillingPreference(billingPreference, contractId);
    dispatch(updateBillingPreferenceSuccess({ billingPreference: uppercaseBillingPreference, currentOpptyIndex }));
  } catch (e) {
    dispatch(updateBillingPreferenceFailure(e));
  }
};

export const updateUserAccount = account => dispatch => {
  dispatch(updatingUser());
  return dispatch(updateUserAccountSuccess(account));
};

export const clearUserError = () => dispatch => {
  dispatch(updatingUser());
  return dispatch(updateUserErrorClear());
};

export const fetchSalesRep = prospectId => async dispatch => {
  try {
    const { data } = await GetSalesRep(prospectId);
    dispatch(fetchSalesRepSuccess(data));
  } catch (error) {
    dispatch(fetchSalesRepFailure(error.message));
  }
};

export const shouldFetchSalesRep = userState => !userState.get('salesRep').get('loaded') && !userState.get('salesRep').get('loading');

export const fetchSalesRepIfNeeded = prospectId => (dispatch, getState) => {
  if (!shouldFetchSalesRep(getState().user)) {
    return null;
  }

  return dispatch(fetchSalesRep(prospectId));
};

export const fetchCapDocs = prospectId => async dispatch => {
  dispatch(fetchingCapDocs());
  try {
    const data = await GetCapDocs(prospectId);
    dispatch(fetchCapDocsSuccess(data));
  } catch (e) {
    dispatch(fetchCapDocsFailure(e));
  }
};

export const shouldFetchCapDocs = userState => !userState.get('capDocsLoaded');

export const fetchCapDocsIfNeeded = prospectId => (dispatch, getState) => {
  if (!shouldFetchCapDocs(getState().user)) {
    return null;
  }

  return dispatch(fetchCapDocs(prospectId));
};

export const fetchCapEsignUrl = (documentId, documentType, customerSigned, isProposalDocument, contactId) => async (dispatch, getState) => {
  dispatch(fetchingCapEsignUrl());
  try {
    const { data: url } = await GetCapEsignUrl(documentId, documentType, customerSigned, contactId);
    dispatch(fetchCapEsignUrlSuccess(url));
  } catch (error) {
    dispatch(fetchCapEsignUrlFailure(error));
  }
};
export const shouldFetchCapEsignUrl = userState => !userState.get('capEsignUrlLoaded');

export const fetchCapEsignUrlIfNeeded = (documentId, documentType, customerSigned, isProposalDocument, contactId) => async (dispatch, getState) => {
  if (!shouldFetchCapEsignUrl(getState().user)) {
    return null;
  }
  return dispatch(fetchCapEsignUrl(documentId, documentType, customerSigned, isProposalDocument, contactId));
};

export const updateEmailSuccess = email => (dispatch, getState) => dispatch(fetchUser());

// SELECTORS
const userSelector = state => state.user;

export const getFriendlyName = createSelector([userSelector], user => (user.get('loaded') ? user.get('firstName') : ''));

export const getUserInfo = createSelector([userSelector], user => {
  let opportunityCount = 0;
  if (user.get('opportunities')) {
    opportunityCount = user.get('opportunities').length;
  }
  return {
    firstName: user.get('firstName'),
    lastName: user.get('lastName'),
    email: user.get('email'),
    phoneNumber: user.get('phoneNumber'),
    mobilePhoneNumber: user.get('mobilePhoneNumber'),
    address: user.get('address'),
    city: user.get('city'),
    state: user.get('state'),
    zip: user.get('zip'),
    country: user.get('country'),
    opportunityCount,
  };
});

export const getProposalId = createSelector([userSelector], user => user.get('contract') && user.get('contract').proposalId);

export const getProposalNumber = createSelector([userSelector], user => user.get('contract') && user.get('contract').proposalNumber);

export const getProspectId = createSelector([userSelector], user => user.get('prospectId'));

export const getContractId = createSelector([userSelector], user => user.get('contract') && user.get('contract').contractId);

export const getServiceContractId = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return oppty?.serviceContractId;
});

export const getContactId = createSelector([userSelector], user => user.get('contactId'));

export const getContactIds = createSelector([userSelector], user => {
  const opportunities = user.get('opportunities');
  if (user.get('opportunities')) {
    const contactIds = opportunities?.map(oppty => oppty?.contactId).filter(Boolean);
    return Array.isArray(contactIds) ? contactIds : [];
  }
  return [];
});

export const getProjectId = createSelector([userSelector], user => user.get('projectId'));

export const getOpptyName = createSelector([userSelector], user => user.get('opptyName'));

export const getSOWId = createSelector([userSelector], user => user.get('sowId'));

export const getContractStatus = createSelector([userSelector], user => user.get('contract') && user.get('contract').status);

export const getACHEligible = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return oppty?.eligibleForAutoPay;
});

export const isEligibleForAutopayByCreditCard = oppty => {
  return Boolean(oppty?.eligibleForAutoPay && oppty?.autopayByCreditCardEligible);
};

export const getEligibleForAutopayByCreditCard = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return isEligibleForAutopayByCreditCard(oppty);
});

export const getHasAutoPay = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return oppty?.hasAutoPay;
});

export const getPrePTOStatus = createSelector([userSelector], user => {
  // eslint does not like inverting the boolean constructor
  // eslint-disable-next-line
  return !Boolean(user.get('contract')?.ptoDate);
});

export const getFailedDeactivationStatus = createSelector([userSelector], user => {
  const status = user.get('contract') && user.get('contract').status;
  return status === FAILED_DEACTIVATION;
});

export const getDeactivatedStatus = createSelector([userSelector], user => {
  const status = user.get('contract') && user.get('contract').status;
  return status === DEACTIVATED;
});

export const getUserAgreementType = createSelector([userSelector], user => user.get('contract') && user.get('contract').agreementType);

export const getUserAgreementProdcutType = createSelector([userSelector], user => user.get('contract') && user.get('contract').agreementProductType);

/*
 * @returns {boolean}
 */
export const getUserMonthlyType = createSelector([userSelector], user => {
  const type = user?.get('contract') && user.get('contract').agreementType?.toLowerCase();
  return !type ? true : !type.includes('customer owned') && !type.includes('full upfront') && !type.includes(PREPAID);
});

export const getSalesRepInfo = createSelector([userSelector], user => user.get('salesRep'));

export const getContract = createSelector([userSelector], user => user.get('contract'));

export const getMainUserLoaded = createSelector([userSelector], user => !user.get('loading') && user.get('loaded'));

export const getUserLoading = createSelector([userSelector], user => user.get('loading') || !user.get('loaded') || user.get('loadingProspect'));

export const getProspectLoaded = createSelector([userSelector], user => user.get('hasProspectLoaded'));

export const getUserUpdating = createSelector([userSelector], user => user.get('updating'));

export const getUserAccounts = createSelector([userSelector], user => user.get('opportunities'));

export const getActiveChartType = createSelector([userSelector], user => {
  const prospect = user.get('prospect');
  return prospect?.activeChart || OLD_GRAPH_VIEW;
});

export const getOpportunity = createSelector([userSelector], user => getCurrentOpptyFromUser(user));

export const getUserPurchasedThru = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return oppty?.contract?.purchasedThru;
});

export const getUserLeadSource = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  return oppty?.contract?.leadSource;
});

export const getCurrentOwner = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  // if the current owner prop isn't available, assume we have the current owner
  return oppty?.isCurrentOwner === undefined ? true : oppty?.isCurrentOwner;
});

export const getRelatedContact = createSelector([userSelector], user => {
  const oppty = getCurrentOpptyFromUser(user);
  // if the related contact isn't available, assume we have the primary contact
  return oppty?.isRelatedContact === undefined ? false : oppty?.isRelatedContact;
});

export const getProspect = createSelector([userSelector], user => user.get('prospect'));

export const getMasterId = createSelector([userSelector], user => {
  // Lots of null checking in here. Master ID might not be available at launch
  // so we have to be careful
  const masterContractInfo = user.get('masterContractInfo');
  if (!masterContractInfo) {
    return null;
  }
  const masterIds = masterContractInfo.filter(info => info.opptyId === user.get('opptyId'));

  if (masterIds.length === 0) {
    return null;
  }
  return masterIds[0].masterId;
});

export const getUserError = createSelector([userSelector], user => user.get('error'));

export const getCapDocs = createSelector([userSelector], user => {
  return user.get('capDocs');
});

export const getCapEsignUrl = createSelector([userSelector], user => {
  return user.get('capEsignUrl');
});

// REDUCER
export const initialState = fromJS({
  loaded: false,
  loading: false,
  loadingProspect: false,
  hasProspectLoaded: false,
  error: null,
  updating: false,
  prospectId: null,
  firstName: '',
  lastName: '',
  email: '',
  contactId: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  opportunities: [],
  salesRep: {
    data: {},
    loaded: false,
    loading: false,
  },
  fetchUserFailed: false,
  capDocs: {},
  capDocsLoaded: false,
  capEsignUrl: null,
  capEsignUrlLoaded: false,
});

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_USER_SUCCESS:
      return state.set('loaded', true).set('loading', false).merge(action.payload);
    case FETCH_USER_FAILURE:
      return state.set('loading', false).set('error', action.payload).set('fetchUserFailed', true);
    case FETCHING_USER:
      return state.set('loading', true);
    case SET_SHOULD_FETCH_USER:
      return state.set('loaded', false).set('loading', false);
    case FETCH_PROSPECT_SUCCESS:
      return state.set('loadingProspect', false).set('hasProspectLoaded', true).set('prospect', action.payload);
    case FETCH_PROSPECT_FAILURE:
      return state.set('loadingProspect', false).set('hasProspectLoaded', true).set('error', action.payload);
    case FETCHING_PROSPECT:
      return state.set('loadingProspect', true).set('hasProspectLoaded', false);
    case UPDATE_PROSPECT_ACTIVE_CHART:
      return state.setIn(['prospect', 'activeChart'], action.activeChart);
    case UPDATING_USER:
      return state.set('updating', true).set('error', null);
    case UPDATE_PHONE_SUCCESS:
      return state.set('updating', false).merge(action.payload);
    case UPDATE_PHONE_FAILURE:
      return state.set('updating', false).set('error', action.payload);
    case UPDATE_USER_ACCOUNT:
      return state.set('updating', false).merge(action.payload);
    case UPDATE_BILLING_PREFERENCE_SUCCESS:
      return state
        .set('error', null)
        .setIn(['opportunities', action.payload.currentOpptyIndex, 'contract', 'billingNotificationPreference'], action.payload.billingPreference)
        .set('updating', false);
    case UPDATE_BILLING_PREFERENCE_FAILURE:
      return state.set('updating', false).set('error', action.payload);
    case UPDATE_MOBILE_PHONE:
      return state.set('updating', false).merge(action.payload);
    case UPDATE_USER_ERROR_CLEAR:
      return state.set('updating', false).set('error', null);
    case FETCHING_SALESREP:
      return state.setIn(['salesRep', 'loading'], true);
    case FETCH_SALESREP_SUCCESS:
      return state.setIn(['salesRep', 'loaded'], true).setIn(['salesRep', 'loading'], false).setIn(['salesRep', 'data'], action.payload);
    case FETCH_SALESREP_FAILURE:
      return state.setIn(['salesRep', 'loading'], false).setIn(['salesRep', 'loaded'], true).set('error', action.payload);
    case FETCH_CAP_DOCS_SUCCESS:
      return state.set('capDocsLoaded', true).set('capDocs', action.payload);
    case FETCH_CAP_DOCS_FAILURE:
      return state.set('capDocsLoaded', true);
    case RESET_CAP_DOCS:
      return state.set('capDocsLoaded', false).set('capDocs', {});
    case FETCH_CAP_ESIGN_URL_SUCCESS:
      return state.set('capEsignUrlLoaded', true).set('capEsignUrl', action.payload);
    case FETCH_CAP_ESIGN_URL_FAILURE:
      return state.set('capEsignUrlLoaded', true);
    case RESET_CAP_ESIGN_URL:
      return state.set('capEsignUrlLoaded', false).set('capEsignUrl', null);
    case SET_PROJECT_ID:
      return state.set('projectId', action.payload);
    case SET_USER_HAS_AUTOPAY_TRUE:
      return state.setIn(['opportunities', action.payload.currentOpptyIndex, 'hasAutoPay'], true);
    default:
      return state;
  }
};
